import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowDownLeft, ArrowUpRight, ChevronLeft, ChevronRight,
  Loader2, TrendingUp, TrendingDown, RefreshCw, Search, X
} from 'lucide-react';

const TYPE_LABELS = {
  topup:            { label: 'Top-Up',        color: 'text-emerald-400', bg: 'bg-emerald-500/15', sign: '+' },
  charge_monthly:   { label: 'Monthly Fee',   color: 'text-red-400',     bg: 'bg-red-500/15',     sign: '-' },
  percent_fee:      { label: 'TX Fee',         color: 'text-amber-400',   bg: 'bg-amber-500/15',   sign: '-' },
  extra_tx:         { label: 'Extra TX',       color: 'text-orange-400',  bg: 'bg-orange-500/15',  sign: '-' },
  admin_adjustment: { label: 'Admin Adj.',     color: 'text-blue-400',    bg: 'bg-blue-500/15',    sign: '' },
};

const TypeBadge = ({ type }) => {
  const cfg = TYPE_LABELS[type] || { label: type, color: 'text-muted-foreground', bg: 'bg-muted' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${cfg.bg} ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

const AmountCell = ({ amount, type }) => {
  const cfg = TYPE_LABELS[type] || {};
  const isCredit = amount > 0;
  const color = isCredit ? 'text-emerald-400' : 'text-red-400';
  const sign = isCredit ? '+' : '';
  return (
    <span className={`font-mono font-semibold text-sm ${color}`}>
      {sign}${Math.abs(amount).toFixed(2)}
    </span>
  );
};

export default function AdminBillingTransactions() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState({ total_credits: 0, total_debits: 0, by_type: {} });
  const [typeFilter, setTypeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const limit = 50;

  const fetchTxs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/billing/admin/balance-transactions?page=${page}&limit=${limit}`;
      if (typeFilter) url += `&tx_type=${typeFilter}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const { data } = await axios.get(url, { headers });
      setTxs(data.data || []);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
      setSummary(data.summary || { total_credits: 0, total_debits: 0, by_type: {} });
    } catch {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, fromDate, toDate, search]);

  useEffect(() => { fetchTxs(); }, [fetchTxs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const clearFilters = () => {
    setTypeFilter('');
    setFromDate('');
    setToDate('');
    setSearch('');
    setSearchInput('');
    setPage(1);
  };

  const hasFilters = typeFilter || fromDate || toDate || search;

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const netFlow = (summary.total_credits || 0) - (summary.total_debits || 0);

  return (
    <AdminLayout title="Billing Transactions">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Total Credits</span>
              <ArrowDownLeft className="h-4 w-4 text-emerald-400" />
            </div>
            <p className="text-xl font-bold text-emerald-400" data-testid="total-credits">
              +${(summary.total_credits || 0).toFixed(2)}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Top-ups & adjustments</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Total Debits</span>
              <ArrowUpRight className="h-4 w-4 text-red-400" />
            </div>
            <p className="text-xl font-bold text-red-400" data-testid="total-debits">
              -${(summary.total_debits || 0).toFixed(2)}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Fees & charges</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Net Flow</span>
              {netFlow >= 0
                ? <TrendingUp className="h-4 w-4 text-emerald-400" />
                : <TrendingDown className="h-4 w-4 text-red-400" />}
            </div>
            <p className={`text-xl font-bold ${netFlow >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="net-flow">
              {netFlow >= 0 ? '+' : ''}{netFlow.toFixed(2)}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Credits minus debits</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Total Records</span>
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-xl font-bold text-foreground" data-testid="total-records">{total}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Matching current filters</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-muted-foreground mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="Merchant ID or description..."
                  className="w-full h-9 pl-8 pr-3 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Type Filter */}
            <div className="min-w-[150px]">
              <label className="text-xs text-muted-foreground mb-1 block">Type</label>
              <select
                value={typeFilter}
                onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
                className="w-full h-9 px-3 text-sm bg-background border border-input rounded-lg focus:outline-none"
                data-testid="type-filter"
              >
                <option value="">All Types</option>
                <option value="topup">Top-Up</option>
                <option value="charge_monthly">Monthly Fee</option>
                <option value="percent_fee">TX Fee</option>
                <option value="extra_tx">Extra TX</option>
                <option value="admin_adjustment">Admin Adjustment</option>
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={e => { setFromDate(e.target.value); setPage(1); }}
                className="h-9 px-3 text-sm bg-background border border-input rounded-lg focus:outline-none"
                data-testid="from-date"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">To</label>
              <input
                type="date"
                value={toDate}
                onChange={e => { setToDate(e.target.value); setPage(1); }}
                className="h-9 px-3 text-sm bg-background border border-input rounded-lg focus:outline-none"
                data-testid="to-date"
              />
            </div>

            <Button type="submit" size="sm" className="h-9" data-testid="search-btn">Search</Button>
            {hasFilters && (
              <Button type="button" variant="outline" size="sm" className="h-9" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : txs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-2">
              <RefreshCw className="h-8 w-8 text-muted-foreground opacity-30" />
              <p className="text-sm text-muted-foreground">No transactions found</p>
              {hasFilters && (
                <Button variant="link" size="sm" onClick={clearFilters}>Clear filters</Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="billing-transactions-table">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Merchant</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Description</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Amount</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Bal. Before</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Bal. After</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((tx, i) => (
                    <tr
                      key={tx.id || i}
                      className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                      data-testid={`tx-row-${i}`}
                    >
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {fmtDate(tx.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground text-[13px] leading-tight">{tx.merchant_name}</p>
                        <p className="text-[11px] text-muted-foreground">{tx.merchant_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <TypeBadge type={tx.type} />
                      </td>
                      <td className="px-4 py-3 text-[13px] text-muted-foreground max-w-[260px] truncate">
                        {tx.description || '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <AmountCell amount={tx.amount} type={tx.type} />
                      </td>
                      <td className="px-4 py-3 text-right text-[13px] font-mono text-muted-foreground">
                        ${(tx.balance_before ?? 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-[13px] font-mono font-semibold text-foreground">
                        ${(tx.balance_after ?? 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && total > limit && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">Page {page} of {totalPages}</span>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
